import { clone, map } from 'lodash';
import WSIssueMedia, {
	InputWSIssueMediaFragmentType,
	WSIssueMediaFragment,
	WSIssueMediaFragmentType,
} from './WSIssueMedia.js';
import WSIssueMediaLoadProductFactory, {
	InputWSIssueMediaLoadProductFactoryType,
	WSIssueMediaLoadProductFactoryTypes,
} from "./WSIssueMediaLoadProductFactory.js";

import WSPromotion, {
	InputWSPromotionType,
	WSPromotionType,
} from './WSPromotion.js';

// Table 2.7.39.1 WSIssueSubsystemMedia Field Descriptions
export default class WSIssueSubsystemMedia extends WSIssueMedia {
	constructor({
		subsystem,
		subsystemAccountReference = null,
		mediaType,
		mediaSKU = null,
		loadProducts = null,
		mediaItemPrice = null,
		feeAmount = null,
		depositAmount = null,
		replacementFeeAmount = null,
		enablementFeeAmount = null,
		shippingFeeAmount = null,
		itemTotalAmount = null,
		replacedTravelTokenId = null,
		replacedTokenReasonCode = null,
		subsystemTokenType = null,
		inventoryPartNumber = null,
		mediaItemPriceOriginal = null,
		mediaItemPriceDiscountAmount = null,
		feeOriginalAmount = null,
		feeDiscountAmount = null,
		depositOriginalAmount = null,
		depositDiscountAmount = null,
		replacementFeeOriginalAmount = null,
		replacementFeeDiscountAmount = null,
		enablementFeeOriginalAmount = null,
		enablementFeeDiscountAmount = null,
		shippingFeeOriginalAmount = null,
		shippingFeeDiscountAmount = null,
		itemTotalOriginalAmount = null,
		itemTotalDiscountAmount = null,
		appliedPromotions = null,
		waiveEnableFeeReasonCode = null,
		waiveEnableFeeFlag = false,

		// Super props
		type,
		operatorId,
		riderClassId,
	}) {
		super({
			type,
			operatorId,
			riderClassId,
		});

		/**
		 * @type String
		 * string(20)
		 * (Required) Unique identifier of the subsystem.
		 */
		this.subsystem = subsystem;

		/**
		 * @type String
		 * string (20)
		 * (Conditionally-Required) Unique identifier for the customer’s account in the subsystem.
		 * If the subsystemAccountReference is not provided, a new subsystem account will be created
		 * and linked to the registered customer with the issued media.
		 * Otherwise, the media will be issued to the specified subsystem account.
		 * subsystemAccountReference is conditionally required when replacedTravelTokenId is provided.
		 */
		this.subsystemAccountReference = subsystemAccountReference;

		/**
		 * @type String
		 * string(50)
		 * (Required) the type of media to issue. See section 2.6.9 Media Typesfor possible values.
		 */
		this.mediaType = mediaType;

		/**
		 * @type {WSIssueMediaLoadProduct[]}
		 * List<WSIssueMediaLoadProduct>
		 * (Optional) the products to be loaded to the new media.
		 */
		this.loadProducts = loadProducts;

		// int
		// (Optional) Price of the media to be collected for the order. This number is the number of the base unit of currency, such as cents.
		this.mediaItemPrice = mediaItemPrice;

		// int
		// (Optional) the media fee amount
		this.feeAmount = feeAmount;

		// int
		// (Optional) Deposit amount for the media
		this.depositAmount = depositAmount;

		// int
		// (Optional) The media replacement fee amount
		this.replacementFeeAmount = replacementFeeAmount;

		/**
		 * @type Number
		 * Int
		 * (Optional) the media fee amount
		 */
		this.enablementFeeAmount = enablementFeeAmount;

		// int
		// (Optional) The individual media shipping fee amount
		this.shippingFeeAmount = shippingFeeAmount;

		/**
		 * @type int
		 * (Required) Total amount in cents for the line item (includes media, deposit and products).
		 */
		this.itemTotalAmount = itemTotalAmount;

		/**
		 * @type String
		 * string(20)
		 * (Optional) The media travel token id being replaced. This field will be used when used in the replacement context.
		 */
		this.replacedTravelTokenId = replacedTravelTokenId;

		// string(40)
		// (Required) Subsystem specific token type for this media type as defined by the subsystem that manages the travel token. For example, for subsystem ABP, Smartcard, Bankcard, OpenTransitRegular. This information is needed for issuing the travel token in the subsystem.
		this.replacedTokenReasonCode = replacedTokenReasonCode;

		/**
		 * @type String
		 * string(40)
		 * (Required) Subsystem specific token type for this media type as
		 * defined by the subsystem that manages the travel token.
		 * For example, for subsystem ABP, Smartcard, Bankcard, OpenTransitRegular.
		 * This information is needed for issuing the travel token in the subsystem.
		 */
		this.subsystemTokenType = subsystemTokenType;

		/**
		 * @type String
		 * string(40)
		 * maxLength: 40
		 * example: UMB-004-M005-000003
		 * The unique identifier for the media attributes to be issued
		 */
		this.mediaSKU = mediaSKU;

		/**
		 * @type String
		 * string(100)
		 * (Optional) Media Product Inventory Part Number.
		 */
		this.inventoryPartNumber = inventoryPartNumber;
		// int
		// (Conditionally-Required) Price of the media after discount. This is equals mediaItemPrice.(Price defined in prod cat before any discount applied.)
		this.mediaItemPriceOriginal = mediaItemPriceOriginal;
		// int
		// (Conditionally-Required) Discount Price of the media. This number is the number of the base unit of currency, such as cents.
		this.mediaItemPriceDiscountAmount = mediaItemPriceDiscountAmount;
		// int
		// (Conditionally-Required) The media fee amount before applying any discounts. This is required when a valid discount code is applied for fee discount.
		this.feeOriginalAmount = feeOriginalAmount;
		// int
		// (Conditionally-Required) The media fee discount amount. This is required when a valid discount code is applied for fee discount.
		this.feeDiscountAmount = feeDiscountAmount;
		// int
		// (Conditionally-Required) Deposit amount for the media before applying any discounts. This is required when a valid discount code is applied for deposite discount.
		this.depositOriginalAmount = depositOriginalAmount;
		// int
		// (Conditionally-Required) Deposit discount amount for the media. This is required when a valid discount code is applied for deposite discount.
		this.depositDiscountAmount = depositDiscountAmount;
		// int
		// (Conditionally-Required) The media replacement fee amount before applying any discount. This is required when a valid discount code is applied for replacement fee discount.
		this.replacementFeeOriginalAmount = replacementFeeOriginalAmount;
		// int
		// (Conditionally-Required) The media replacement discount fee amount. This is required when a valid discount code is applied for replacement fee discount.
		this.replacementFeeDiscountAmount = replacementFeeDiscountAmount;
		// int
		// (Conditionally-Required) The media enablement fee amount before applying any discount. This is required when a valid discount code is applied for enablement fee discount.
		this.enablementFeeOriginalAmount = enablementFeeOriginalAmount;
		// int
		// (Conditionally-Required) The media enablement discount fee amount. This is required when a valid discount code is applied for enablement fee discount.
		this.enablementFeeDiscountAmount = enablementFeeDiscountAmount;
		// int
		// (Conditionally-Required) The individual media shipping fee before applying any discount. This is required when a valid discount code is applied for shipping fee discount.
		this.shippingFeeOriginalAmount = shippingFeeOriginalAmount;
		// int
		// (Conditionally-Required) The individual media shipping fee discount amount. This is required when a valid discount code is applied for shipping fee discount.
		this.shippingFeeDiscountAmount = shippingFeeDiscountAmount;
		// int
		// (Conditionally-Required) Total amount before applying any discount in cents for the line item (includes media, deposit fees and products). This is required when discount code is applied.
		this.itemTotalOriginalAmount = itemTotalOriginalAmount;
		// int
		// (Conditionally-Required) Total discount amount in cents for the line item (includes media, deposit fees and products). This is required when discount code is applied.
		this.itemTotalDiscountAmount = itemTotalDiscountAmount;

		// boolean
		// Default value is false. Set this to true if the enablement fee has already been paid on the missing card that is being replaced.
		this.waiveEnableFeeFlag = waiveEnableFeeFlag;

		// string
		// Reason code for waived enablement fee
		this.waiveEnableFeeReasonCode = waiveEnableFeeReasonCode;

		/**
		 * List<WSPromotion>
		 * (Optional) List of promotions applied.
		 * @type {WSPromotion[]}
		 */
		this.appliedPromotions = appliedPromotions;
		this.inventoryPartNumber = inventoryPartNumber;

		/**
		 * @type {Int}
		 * (Optional) The rider class
		 */
		this.riderClassId = riderClassId;
	}

	static fromBackoffice(data) {

		const result = new WSIssueSubsystemMedia({
			...data,
			...super.fromBackoffice(data),
			loadProducts: data.loadProducts?.map(loadProduct => WSIssueMediaLoadProductFactory.fromBackoffice(loadProduct))
				?? null,
			appliedPromotions: data.appliedPromotions?.map(wsPromotion => WSPromotion.fromBackoffice(wsPromotion))
				?? null,
		});

		return result;
	}

	static fromSerialized(data) {
		const result = new WSIssueSubsystemMedia({
			...data,
			...super.fromSerialized(data),
			loadProducts: data.loadProducts?.map(loadProduct => WSIssueMediaLoadProductFactory.fromSerialized(loadProduct))
					?? null,
			appliedPromotions: data.appliedPromotions?.map(wsPromotion => WSPromotion.fromSerialized(wsPromotion))
					?? null,
		});

		return result;
	}

	toBackoffice() {
		const result = clone(this);

		result.loadProducts = this.loadProducts
			? map(this.loadProducts, loadProduct => loadProduct.toBackoffice())
			: null;

		result.appliedPromotions = this.appliedPromotions?.map(wsPromotion => wsPromotion.toBackoffice())
			?? null;

		return result;
	}

	toResolver() {
		const forGraphql = clone(this);

		forGraphql.loadProducts = this.loadProducts
			? map(this.loadProducts, loadProduct => loadProduct.toResolver())
			: null;

		forGraphql.appliedPromotions = this.appliedPromotions?.map(wsPromotion => wsPromotion.toBackoffice())
			?? null;

		return forGraphql;
	}
}

export const WSIssueSubsystemMediaFragmentType = [];

export const InputWSIssueSubsystemMediaFragmentType = WSIssueSubsystemMediaFragmentType;

export const WSIssueSubsystemMediaFragment = `
	subsystem: String!
	mediaSKU: String
	subsystemAccountReference: String
	mediaType: String!
	# loadProducts has separate input/output
	mediaItemPrice: Int
	feeAmount: Int
	depositAmount: Int
	replacementFeeAmount: Int
	enablementFeeAmount: Int
	shippingFeeAmount: Int
	itemTotalAmount: Int
	replacedTravelTokenId: String
	replacedTokenReasonCode: String
	subsystemTokenType: String
	inventoryPartNumber: String
	mediaItemPriceOriginal: Int
	mediaItemPriceDiscountAmount: Int
	feeOriginalAmount: Int
	feeDiscountAmount: Int
	depositOriginalAmount: Int
	depositDiscountAmount: Int
	replacementFeeOriginalAmount: Int
	replacementFeeDiscountAmount: Int
	enablementFeeOriginalAmount: Int
	enablementFeeDiscountAmount: Int
	shippingFeeOriginalAmount: Int
	shippingFeeDiscountAmount: Int
	itemTotalOriginalAmount: Int
	itemTotalDiscountAmount: Int
	waiveEnableFeeFlag: Boolean
	waiveEnableFeeReasonCode: String
	# appliedPromotions has separate input/output
`;

export const InputWSIssueSubsystemMediaType = [
	...InputWSIssueMediaFragmentType,
	...InputWSIssueSubsystemMediaFragmentType,
	...InputWSIssueMediaLoadProductFactoryType,
	...InputWSPromotionType,
	`
		input InputWSIssueSubsystemMedia {
			${WSIssueMediaFragment}
			${WSIssueSubsystemMediaFragment}
			loadProducts: [ InputWSIssueMediaLoadProductFactory! ]
			appliedPromotions: [ InputWSPromotion! ]
		}
	`,
];

export const WSIssueSubsystemMediaType = [
	...WSIssueMediaFragmentType,
	...WSIssueSubsystemMediaFragmentType,
	...WSIssueMediaLoadProductFactoryTypes,
	...WSPromotionType,
	`
		type WSIssueSubsystemMedia implements WSIssueMediaFactory {
			${WSIssueMediaFragment}
			${WSIssueSubsystemMediaFragment}
			loadProducts: [ WSIssueMediaLoadProductFactory! ]
			appliedPromotions: [ WSPromotion! ]
		}
	`,
];
